import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "api";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { formatDate, download, debug } from "utils";
import FormalityRequestPaymentModal from "components/formalities/modals/FormalityRequestPaymentModal";
import FormalityRequestSummaryModal from "components/formalities/modals/FormalityRequestSummaryModal";
import FormalityRequestStatusTimeline from "components/formalities/helpers/FormalityRequestStatusTimeline";
import { loadCart } from '../../redux/cartSlice';
import {
  FiEdit,
  FiEye,
  FiFileText,
  FiDownload,
  FiTrash2
} from "react-icons/fi";

import { HiOutlineCreditCard } from "react-icons/hi2";
import Swal from "sweetalert2";

import './assets/formalities-requests-list.css';
export default function FormalitiesRequestsList({ filters = {} }) {
  const navigate = useNavigate();
  const config = useSelector((state) => state.config);
  const statuses = config?.formalities?.requests?.status || {};
  const dispatch = useDispatch();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsRequestId, setDocumentsRequestId] = useState(null);
  const {
    search = "",
    month = "",
    status = "all",
  } = filters;
  // -----------------------------
  // LOAD
  // -----------------------------
  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(
          "/formalities/requests?withOrders=1&withTemplates=1"
        );
        setRequests(res.data || []);
      } catch (e) {
        console.error("LOAD USER REQUESTS ERROR", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);
  // -----------------------------
  // NORMALIZE
  // -----------------------------
  const normalize = (str = "") =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  // -----------------------------
  // FILTER
  // -----------------------------
  const filtered = useMemo(() => {
    let items = [...requests];
    if (status !== "all") {
      items = items.filter((r) => r.frStatus === status);
    }
    if (month) {
      items = items.filter((r) =>
        r.frCreatedAt?.startsWith(month)
      );
    }
    if (search.trim()) {
      const s = normalize(search);
      items = items.filter((r) => {
        const title = normalize(
          r.frCustomName ||
          r.formatMeta?.name ||
          r.frTemplateSlug ||
          ""
        );
        const id = normalize(r.frId);
        return title.includes(s) || id.includes(s);
      });
    }
    return items;
  }, [requests, search, month, status]);
  // -----------------------------
  // ACTIONS
  // -----------------------------
  const openRequest = (req) => {
    navigate(`/formalities/request/${req.frId}`);
  };
  const handleDownloadInvoice = (orderId) => {
    download(`/orders/${orderId}/invoice/download`);
  }
  const handleDownloadDocuments = async (frId) => {
    setDocumentsRequestId(frId);
    setDocuments([]);
    setDocumentsLoading(true);
    setShowDocumentsModal(true);
    try {
      const res = await api.get(
        `/formalities/request/${frId}/response/documents`
      );
      // uniquement les documents visibles client et actifs
      const docs = (res.data || []).filter(
        d =>
          d.frdVisibleForCustomer === "1" &&
          d.frdStatus === "active"
      );
      setDocuments(docs);
    } catch (e) {
      console.error("LOAD RESPONSE DOCUMENTS ERROR", e);
    } finally {
      setDocumentsLoading(false);
    }
  };
  const handleLaunchPayment = (req) => {
    setCurrentRequestId(req.frId);
    setShowStripeModal(true);
  };
  const handleShowSummary = (req) => {
    setCurrentRequestId(req.frId);
    setShowSummaryModal(true);
  };
  const handlePaid = (updatedRequest) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.frId === updatedRequest.frId ? updatedRequest : r
      )
    );
  };

  const handleDelete = useCallback(async (requestId) => {
    const conf = await Swal.fire({
      title: "Supprimer la demande",
      html: "Confirmez-vous la supression définitive de cette demande&nbsp;de&nbsp;formalité&nbsp;?",
      showCancelButton: true,
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-danger me-2",
        cancelButton: "btn btn-outline-secondary",
      },
    });

    if (!conf.isConfirmed) return;

    try {
      api.handleError = false;

      await api.delete(`/formalities/request/${requestId}`);
      dispatch(loadCart());
      setRequests(prev =>
        prev.filter(r => r.frId !== requestId)
      );

      await Swal.fire({
        title: "Demande supprimée",
        text: "La demande a été supprimée.",

        confirmButtonText: "OK",
      });

    } catch (e) {
      const message =
        e?.response?.data?.error?.message ||
        "La demande n'a pas pu être supprimée.";

      Swal.fire({
        title: "Erreur",
        html: message,
        confirmButtonText: "OK",
      });
    }
  }, []);



  // -----------------------------
  // RENDER
  // -----------------------------
  if (loading) {
    return <div className="py-5 text-center">Chargement…</div>;
  }
  if (!filtered.length) {
    return (
      <p className="text-muted py-5 text-center">
        Aucune demande trouvée.
      </p>
    );
  }
  return (
    <>
      <div className="row g-4">
        {filtered.map((req) => (
          <div
            key={req.frId}
            className="col-12"
          >
            <div className="fr-card card shadow-sm h-100">
              <div className="fr-card-header card-header bg-primary-10 d-flex justify-content-between align-items-center">
                <h5 className="fs-6 text-truncate m-0 p-0 flex-grow-1">
                  {req.frCustomName || req.template?.ftName || "(Sans titre)"}
                </h5>
                <div className="frSiren text-muted text-end small">SIREN : {req.frSiren}</div>
              </div>
              <div className="card-body pt-2">
                {req.template && (
                  <div className="text-muted small text-truncate mb-2">
                    {req.template?.category?.fcName && (
                      <span>
                        {req.template.category.fcName} /{" "}
                      </span>
                    )}
                    {req.template?.ftName}
                  </div>
                )}
                <div className="small text-muted mb-3">
                  Créée le{" "}
                  {formatDate(req.frCreatedAt, "d/m/Y H:i")}
                </div>
                <FormalityRequestStatusTimeline currentStatus={req.frStatus} />
              </div>
              <div className="fr-card-footer-icons border-top d-flex align-items-center gap-1 justify-content-between w-100 px-2 py-2 ">
                <div >

                  {req.frStatus === "validated" &&
                    req.order?.orderBalance > 0 && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        title="Payer"
                        onClick={() => handleLaunchPayment(req)}
                      >
                        <HiOutlineCreditCard size={16} className="me-1" />Payer maintenant
                      </button>
                    )}
                  {req.frStatus === "completed" && (
                    <button className="btn btn-outline-primary btn-sm " title="Télécharger"
                      onClick={e => handleDownloadDocuments(req.frId)}
                    >
                      <FiDownload size={16} className="me-1" />Mes documents
                    </button>

                  )}
                </div>
                <div className="d-flex gap-2 justify-content-end">

                  {['draft', 'waiting'].includes(req.frStatus) ? (
                    <button
                      className={`btn btn-sm rounded-pill ${req.frStatus === 'waiting'
                        ? 'btn-outline-danger'
                        : 'btn-outline-primary'
                        }`}
                      onClick={() => openRequest(req)}
                      title="Modifier / compléter la demande"
                    >
                      <FiEdit size={16} />
                    </button>
                  ) : (
                    <button
                      className="btn btn-outline-primary btn-sm rounded-pill"
                      title="Synthèse de la demande"
                      onClick={() => handleShowSummary(req)}
                    >
                      <FiEye size={16} />
                    </button>
                  )}
                  {req.order?.orderId && <button
                    className="btn btn-outline-primary btn-sm rounded-pill"
                    title="Télécharger la facture"
                    onClick={() => handleDownloadInvoice(req.order?.orderId)}
                  >
                    <FiFileText size={16} />
                  </button>}
                  {['draft', 'validated'].includes(req.frStatus) &&
                    <button
                      className="btn btn-outline-danger btn-sm rounded-pill"
                      title="Supprimer cette formalité"
                      onClick={() => handleDelete(req.frId)}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  }
                </div>
              </div>

            </div>
          </div>
        ))}
      </div >
      <FormalityRequestPaymentModal
        show={showStripeModal}
        requestId={currentRequestId}
        onPaid={handlePaid}
        onClose={() => setShowStripeModal(false)}
      />
      {
        showSummaryModal && (
          <FormalityRequestSummaryModal
            requestId={currentRequestId}
            show={showSummaryModal}
            onClose={() => setShowSummaryModal(false)}
          />
        )
      }
      {
        showDocumentsModal && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div className="modal fade show d-block" tabIndex="-1">
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Documents à télécharger
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowDocumentsModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    {documentsLoading && (
                      <div className="text-center py-4">
                        Chargement…
                      </div>
                    )}
                    {!documentsLoading && !documents.length && (
                      <p className="text-muted">
                        Aucun document disponible.
                      </p>
                    )}
                    {!documentsLoading && documents.length > 0 && (
                      <ul className="list-group">
                        {documents.map((doc) => (
                          <li
                            key={doc.frdId}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <div className="fw-semibold">
                                {doc.frdLabel || "Document"}
                              </div>
                              {doc.frdDescription && (
                                <div className="small text-muted">
                                  {doc.frdDescription}
                                </div>
                              )}
                            </div>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={() =>
                                download(
                                  `/formalities/response/document/${doc.frdId}/download`
                                  , true)
                              }
                            >
                              <FiDownload size={16} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowDocumentsModal(false)}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      }
    </>
  );
}
